import React, { useState, useEffect } from "react"
import Select from "react-select";
import { axios_helper_async } from "helpers/Axios";
import { ElementRequestFormViewModel } from "Common/ViewModels/ElementRequestFormViewModel";
import { Category } from "Common/Models/Category";
import { Brands } from "Common/Models/Brands";
import withOverlay, { WithOverlayProps } from "Common/withOverlay";
import { MessageType } from "Common/Components/Messages/messageTypes";
import withNotifications, { WithNotificationsProps } from "Common/withNotifications";
import withMessages, { WithMessagesProps } from "Common/withMessages";
import { WithTranslationProps } from "Common/Components/Translation/WithTranslationProps";
import withTranslation from "Common/withTranslation";
import { NotificationType } from "Common/Components/Notifications/notificationTypes";
import { Options } from "Common/Models/Options";
import LoadingButton from "Common/Components/LoadingOverlay/LoadingButton/LoadingButton";

const modelFields: Record<keyof ElementRequestFormViewModel, string> = {
    brand: 'brand',
    category: 'category',
    description: 'description',
    email: 'email',
    name: 'name',
    numberPart: 'numberPart',
    brands: '',
    otherBrand: 'otherBrand',
    categories: '',
    otherCategory: 'otherCategory',
    phone: 'phone',
    quantity: 'quantity',
    errors: 'errors'
};

const ElementRequestForm: React.FC<WithNotificationsProps & WithMessagesProps & WithTranslationProps> = ({
    messages,
    notifications,
    t,
    i18n
}) => {
    const initialState: ElementRequestFormViewModel = {
        brand: '',
        category: '',
        description: '',
        email: '',
        name: '',
        numberPart: '',
        brands: [],
        otherBrand: '',
        categories: [],
        otherCategory: '',
        phone: '',
        quantity: 0,
        errors: {}
    };
    const [model, setModel] = useState<ElementRequestFormViewModel>({
        brand: '',
        category: '',
        description: '',
        email: '',
        name: '',
        numberPart: '',
        brands: [],
        otherBrand: '',
        categories: [],
        otherCategory: '',
        phone: '',
        quantity: 0,
        errors: {}
    });
    const [resetModel, setResetModel] = useState(false);
    const [hiddeOtherBrand, setHiddeOtherBrand] = useState(true);
    const [hiddeOtherCategory, setHiddeOtherCategory] = useState(true);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setModel(prevModel => ({
            ...prevModel,
            [name]: value
        }));
    };
    const handleSelectChange = (selectedOption: any, actionMeta: any) => {
        setModel(prevModel => ({
            ...prevModel,
            [actionMeta.name]: selectedOption ? selectedOption.label : null // Asegúrate de manejar el caso nulo
        }));

        if (actionMeta.name === modelFields.brand) setHiddeOtherBrand(!(selectedOption.value === ''));
        if (actionMeta.name === modelFields.category) setHiddeOtherCategory(!(selectedOption.value === ''));
    };
    const getBrands = async () => {
        try {
            setIsLoading(true);
            const response = await axios_helper_async<Options[]>('/SOI/Get-Brands-Options', 'GET');
    
            notifications.setNotifications(response.notifications);
            messages.setMessages(response.messages);

            if (response.isSuccess && response.data) {
                const values = response.data;
                values.push({
                    value: "",
                    label: t('General.Other')
                });
    
                setModel(prevModel => ({
                    ...prevModel,
                    brands: values
                }));
            }
        }
        catch(ex: any) {
            notifications.showNotification(`${ex}`, NotificationType.Error);
        }
        finally{
            setIsLoading(false);
        }
    };

    const getCategories = async () => {
        try{
            setIsLoading(true);
            const body = {
                language: i18n.language
            }
    
            const response = await axios_helper_async<Options[]>('/SOI/Get-Categories-Options', 'GET', body);
    
            notifications.setNotifications(response.notifications);
            messages.setMessages(response.messages);

            if (response.isSuccess && response.data) {
                const values = response.data;
                values.push({
                    value: "",
                    label: t('General.Other')
                });
    
                setModel(prevModel => ({
                    ...prevModel,
                    categories: values
                }));
            }
        }
        catch(ex: any) {
            notifications.showNotification(`${ex}`, NotificationType.Error);
        }
        finally{
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (resetModel) {
            setModel(initialState);
            // (async () => {                                
            //     await getBrands();
            //     await getCategories();
            // })();
            setResetModel(false);
        }
    }, [resetModel]);

    useEffect(() => {
        (async () => {
            await getBrands();
            await getCategories();
        })();
    }, [i18n.language])

    useEffect(() => {
        setResetModel(true);
    }, []);


    const validateModel = () => {
        let errors: ElementRequestFormViewModel['errors'] = {};
        if (!model.brand) errors.brand = 'ElementRequestForm.Validations.Brand';
        if (!model.category) errors.category = 'ElementRequestForm.Validations.Category';
        if (!model.description) errors.description = 'ElementRequestForm.Validations.Description';
        if (!model.email) errors.email = 'ElementRequestForm.Validations.Email';
        if (!model.name) errors.name = 'ElementRequestForm.Validations.Name';
        if (!model.numberPart) errors.numberPart = 'ElementRequestForm.Validations.NumberPart';
        if (!model.quantity) errors.quantity = 'ElementRequestForm.Validations.Quantity';
        setModel(prevModel => ({
            ...prevModel,
            errors
        }));

        if (Object.keys(errors).length !== 0) notifications.showNotification('General.CompleteData', NotificationType.Error);

        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async () => {
        try {
            setIsLoading(true);
            if (validateModel()) {
                let response = await axios_helper_async<string>('/SOI/Send-Element-Request', 'POST', model);
    
                notifications.setNotifications(response.notifications);
                messages.setMessages(response.messages);

                if (!response.isSuccess) {
                    notifications.showNotification(t('AxiosAPI.InternalError'), NotificationType.Error);
                }
                else {
                    notifications.showNotification(t('ElementRequestForm.Alerts.EmailSent'), NotificationType.Success);
                    setModel(initialState);
                    setResetModel(true);
                }
            }
        }
        catch(ex: any) {
            notifications.showNotification(`${ex}`, NotificationType.Error);
        }
        finally{
            setIsLoading(false);
        }        
    };

    return (
        <React.Fragment>
            <div className="relative py-14">
                <h1 className="mb-6 leading-normal text-center" data-aos="fade-left" data-aos-delay="300"><span className="relative inline-block px-2 mx-2 before:block before:absolute before:-inset-1 before:-skew-y-6 before:bg-sky-50 dark:before:bg-sky-500/20 before:rounded-md before:backdrop-blur-xl"><span className="relative text-custom-500">{t('ElementRequestForm.Title1')}</span></span></h1>
                <div className="grid grid-cols-1 gap-5 py-14">
                    <div className="" data-aos="fade-left" data-aos-delay="300">
                        <form id="MainForm">
                            <div className="grid grid-cols-1 gap-5 md:grid-cols-2 mb-2">
                                {/* Name input */}
                                <div>
                                    <label htmlFor={modelFields.name} className="inline-block mb-2 text-base font-medium text-">{t('ElementRequestForm.Name')} <span className="text-red-500">*</span></label>
                                    <input
                                        type="text"
                                        id={modelFields.name}
                                        name={modelFields.name}
                                        value={model.name}
                                        onChange={handleChange}
                                        className={`form-input ${model.errors.name ? "border-red-500" : "border-slate-200"}`}
                                        placeholder={t('ElementRequestForm.Name')}
                                    />
                                    {model.errors.name && <div className="mt-1 text-sm text-red-500">{t(model.errors.name)}</div>}
                                </div>
                                {/* Email input */}
                                <div>
                                    <label htmlFor={modelFields.email} className="inline-block mb-2 text-base font-medium">{t('ElementRequestForm.Email')} <span className="text-red-500">*</span></label>
                                    <input
                                        type="email"
                                        id={modelFields.email}
                                        name={modelFields.email}
                                        value={model.email}
                                        onChange={handleChange}
                                        className={`form-input ${model.errors.email ? "border-red-500" : "border-slate-200"}`}
                                        placeholder={t('ElementRequestForm.Email')}
                                    />
                                    {model.errors.email && <div className="mt-1 text-sm text-red-500">{t(model.errors.email)}</div>}
                                </div>

                                {/* Phone input */}
                                <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                                    <div>
                                        <label htmlFor={modelFields.phone} className="inline-block mb-2 text-base font-medium">{t('ElementRequestForm.Phone')}</label>
                                        <input
                                            type="tel"
                                            id={modelFields.phone}
                                            name={modelFields.phone}
                                            value={model.phone}
                                            onChange={handleChange}
                                            placeholder={t('ElementRequestForm.Phone')}
                                            className="form-input border-slate-200 container"
                                        />
                                    </div>
                                    <div>                                    
                                        <label htmlFor={modelFields.quantity} className="inline-block mb-2 text-base font-medium text-center">{t('ElementRequestForm.Quantity')}<span className="text-red-500">*</span></label>
                                        <input
                                            type="number"
                                            id={modelFields.quantity}
                                            name={modelFields.quantity}
                                            value={model.quantity}
                                            onChange={handleChange}
                                            placeholder={t('ElementRequestForm.Quantity')}
                                            className={`form-input ${model.errors.quantity ? "border-red-500" : "border-slate-200"}`}
                                        />
                                        {model.errors.quantity && <div className="mt-1 text-sm text-red-500">{t(model.errors.quantity)}</div>}
                                    </div>
                                </div>

                                {/* NumberPart input */}
                                <div>
                                    <label htmlFor={modelFields.numberPart} className="inline-block mb-2 text-base font-medium">{t('ElementRequestForm.NumberPart')} <span className="text-red-500">*</span></label>
                                    <input
                                        type="text"
                                        id={modelFields.numberPart}
                                        name={modelFields.numberPart}
                                        value={model.numberPart}
                                        onChange={handleChange}
                                        className={`form-input ${model.errors.numberPart ? "border-red-500" : "border-slate-200"}`}
                                        placeholder={t('ElementRequestForm.NumberPart')}
                                    />
                                    {model.errors.numberPart && <div className="mt-1 text-sm text-red-500">{t(model.errors.numberPart)}</div>}
                                </div>

                                {/* Brand Select */}
                                <div className={!hiddeOtherCategory && hiddeOtherBrand ? "col-span-full" : ""}>
                                    <label htmlFor={modelFields.brand} className="inline-block mb-2 text-base font-medium text-">{t('ElementRequestForm.Brand')} <span className="text-red-500">*</span></label>
                                    <Select
                                        className={`${model.errors.brand ? "border-red-500" : "border-slate-200"}`}
                                        name={modelFields.brand}
                                        options={model.brands}
                                        placeholder={t('General.SelectItem')}
                                        onChange={handleSelectChange}                                        
                                    />
                                    {model.errors.brand && <div className="mt-1 text-sm text-red-500">{t(model.errors.brand)}</div>}
                                </div>

                                {/* OtherBrand input */}
                                <div className={hiddeOtherBrand ? 'hidden' : ''}>
                                    <label htmlFor={modelFields.otherBrand} className="inline-block mb-2 text-base font-medium">{t('ElementRequestForm.Other')}</label>
                                    <input
                                        type="text"
                                        id={modelFields.otherBrand}
                                        name={modelFields.otherBrand}
                                        value={model.otherBrand}
                                        onChange={handleChange}
                                        className="form-input border-slate-200"
                                        placeholder={t('ElementRequestForm.Other')}
                                    />
                                    {/* {model.errors.otherBrand && !model.brand && <div className="mt-1 text-sm text-red-500">{t(model.errors.otherBrand)}</div>} */}
                                </div>

                                {/* Category Select */}
                                <div className={hiddeOtherCategory && !hiddeOtherBrand ? "col-span-full" : ""}>
                                    <label htmlFor={modelFields.category} className="inline-block mb-2 text-base font-medium">{t('ElementRequestForm.Category')} <span className="text-red-500">*</span></label>
                                    <Select
                                        className={`${model.errors.category ? "border-red-500" : "border-slate-200"}`}
                                        name={modelFields.category}
                                        options={model.categories}
                                        placeholder={t('General.SelectItem')}
                                        onChange={handleSelectChange}
                                    />
                                    {model.errors.category && <div className="mt-1 text-sm text-red-500">{t(model.errors.category)}</div>}
                                </div>


                                {/* OtherCategory input */}
                                <div className={hiddeOtherCategory ? 'hidden' : ''}>
                                    <label htmlFor={modelFields.otherCategory} className="inline-block mb-2 text-base font-medium">{t('ElementRequestForm.Other')}</label>
                                    <input
                                        type="text"
                                        id={modelFields.otherCategory}
                                        name={modelFields.otherCategory}
                                        value={model.otherCategory}
                                        onChange={handleChange}
                                        className="form-input border-slate-200"
                                        placeholder={t('ElementRequestForm.Other')}
                                        hidden={hiddeOtherCategory}
                                    />
                                    {/* {model.errors.otherCategory && !model.category && <div className="mt-1 text-sm text-red-500">{t(model.errors.otherCategory)}</div>} */}
                                </div>
                            </div>
                            <div className="grid grid-cols-1 gap-5">
                                {/* Description textarea */}
                                <div>
                                    <label htmlFor={modelFields.description} className="inline-block mb-2 text-base font-medium">{t('ElementRequestForm.Description')} <span className="text-red-500">*</span></label>
                                    <textarea
                                        id={modelFields.description}
                                        name={modelFields.description}
                                        rows={5}
                                        value={model.description}
                                        onChange={handleChange}
                                        className={`form-input ${model.errors.description ? "border-red-500" : "border-slate-200"}`}
                                        placeholder={t('ElementRequestForm.Description')}
                                    ></textarea>
                                    {model.errors.description && <div className="mt-1 text-sm text-red-500">{t(model.errors.description)}</div>}
                                </div>
                            </div>
                            <div className="mt-5 flex justify-center">
                                {isLoading ?
                                    <LoadingButton />
                                    :
                                    <button type="button" onClick={handleSubmit} className="text-white btn bg-custom-500 border-custom-500 w-100">{t('ElementRequestForm.BtnSendMessage')}</button>
                                }
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </React.Fragment>
    )
};

export default withMessages(withNotifications(withTranslation(ElementRequestForm)));